import React, { useContext, useEffect, useState } from "react";
import {
  FiRefreshCw,
  FiPlus,
  FiUploadCloud,
  FiCheckCircle,
} from "react-icons/fi";
import { toast } from "react-hot-toast";
import useClients from "../../../Services/ClientServices/ClientConfig/useClientConfig";
import { GlobalContext } from "../../../../Context/GlobalContext";
import { uploadItemImage } from "../../../../Utils/imageUploadUtils";

/* ---------------- CONSTANTS ---------------- */
const BORDER_SMART = "border-gray-200";

const createEmptyRow = () => ({
  backend_id: "",
  certificate_name: "",
  certificate_image: "",
  status: "Y",
});

function CertificatesSection({ clientId }) {
  const { anlookApi } = useContext(GlobalContext);
  const { getCertificates, updateCertificate } = useClients();

  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingKey, setUpdatingKey] = useState(null);

  const fetchCertificates = async () => {
    setLoading(true);

    const apiData = await getCertificates(clientId);

    const mapped = Array.isArray(apiData)
      ? apiData.map((c) => ({
          backend_id: c.id || c.certificate_id || "",
          certificate_name: c.certificate_name || "",
          certificate_image: c.certificate_image || "",
          status: String(c.status || "Y").toUpperCase() === "N" ? "N" : "Y",
        }))
      : [];

    setCertificates([...mapped, createEmptyRow()]);
    setLoading(false);
  };

  useEffect(() => {
    if (clientId) fetchCertificates();
  }, [clientId]);

  const handleChange = (index, field, value) => {
    setCertificates((prev) =>
      prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)),
    );
  };

  const toggleStatus = (index) => {
    setCertificates((prev) =>
      prev.map((c, i) =>
        i === index
          ? {
              ...c,
              status: c.status === "Y" ? "N" : "Y",
            }
          : c,
      ),
    );
  };

  const handleFileUpload = async (index, file, event) => {
    if (!file) return;

    if (!clientId) {
      toast.error("Client id missing");

      if (event?.target) {
        event.target.value = "";
      }

      return;
    }

    const toastId = toast.loading("Uploading image...");

    try {
      const result = await uploadItemImage({
        anlookApi,
        userid: clientId,
        itemName: "cert",
        file,
        maxSizeMB: 2,
      });

      const imageUrl = result?.imageUrl;

      if (!imageUrl) {
        throw new Error("Image URL missing");
      }

      handleChange(index, "certificate_image", imageUrl);

      toast.success("Image uploaded", {
        id: toastId,
      });
    } catch (error) {
      console.error("Certificate image upload error:", error);

      toast.error(error?.message || "Upload failed", {
        id: toastId,
      });
    } finally {
      if (event?.target) {
        event.target.value = "";
      }
    }
  };

  const saveCertificate = async (cert, index) => {
    if (!cert.certificate_name?.trim()) {
      toast.error("Certificate name required");
      return;
    }

    const rowKey = cert.backend_id || `new-${index}`;
    setUpdatingKey(rowKey);

    const success = await updateCertificate({
      userid: clientId,
      certificate_id: cert.backend_id || "",
      certificate_name: cert.certificate_name.trim(),
      certificate_image: cert.certificate_image || "",
      status: cert.status === "N" ? "N" : "Y",
    });

    if (success) {
      fetchCertificates();
    }

    setUpdatingKey(null);
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
      {/* TOP BAR */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <div>
          <h3 className="text-[10px] font-black uppercase tracking-wider text-gray-500">
            Business Credentials
          </h3>
          <p className="mt-0.5 text-[10px] font-semibold text-gray-500">
            Manage certifications and recognition
          </p>
        </div>

        <button
          type="button"
          onClick={() =>
            setCertificates((prev) => {
              const hasEmptyRow = prev.some(
                (c) =>
                  !c.backend_id &&
                  !c.certificate_name?.trim() &&
                  !c.certificate_image,
              );

              if (hasEmptyRow) return prev;

              return [...prev, createEmptyRow()];
            })
          }
          className="inline-flex h-7 items-center gap-1 rounded-md bg-[#01C37D] px-2.5 text-[10px] font-black uppercase tracking-wide text-white shadow-sm shadow-[#01C37D]/20 hover:bg-[#00ad70] active:scale-95"
        >
          <FiPlus size={12} strokeWidth={3} />
          Add
        </button>
      </div>

      {/* LOADING */}
      {loading && certificates.length === 0 ? (
        <div className="flex h-24 items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-4 border-[#01C37D]/20 border-t-[#01C37D]" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
          {certificates.map((cert, index) => {
            const rowKey = cert.backend_id || `new-${index}`;
            const isSaving = updatingKey === rowKey;
            const isActive = cert.status === "Y";

            return (
              <div
                key={rowKey}
                className="min-w-0 rounded-lg border border-gray-200 bg-[#F8FBFF] p-2 transition-all hover:border-[#01C37D]/40 hover:bg-white hover:shadow-md"
              >
                {/* TOGGLE + UPDATE BUTTON ABOVE INPUT */}
                <div className="mb-2 flex items-center justify-between gap-1 border-b border-gray-200 pb-2">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => toggleStatus(index)}
                      className={`relative h-5 w-9 rounded-full transition-all ${
                        isActive ? "bg-emerald-500" : "bg-gray-300"
                      }`}
                      title={isActive ? "Active" : "Inactive"}
                    >
                      <span
                        className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${
                          isActive ? "left-5" : "left-0.5"
                        }`}
                      />
                    </button>

                    <span
                      className={`rounded-full px-1.5 py-0.5 text-[8px] font-black uppercase ${
                        isActive
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-gray-200 text-gray-500"
                      }`}
                    >
                      {isActive ? "Y" : "N"}
                    </span>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => saveCertificate(cert, index)}
                      disabled={isSaving}
                      className={`inline-flex h-6 items-center gap-1 rounded-md border px-1.5 text-[8px] font-black uppercase tracking-wide transition-all active:scale-95 disabled:opacity-60 ${
                        cert.backend_id
                          ? "border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                          : "border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100"
                      }`}
                      title={
                        cert.backend_id
                          ? "Update Certificate"
                          : "Save Certificate"
                      }
                    >
                      <FiRefreshCw
                        size={11}
                        className={isSaving ? "animate-spin" : ""}
                      />
                      {cert.backend_id ? "Update" : "Save"}
                    </button>

                    {cert.backend_id && (
                      <div
                        className="flex h-6 w-5 items-center justify-center text-[#01C37D]"
                        title="Saved Record"
                      >
                        <FiCheckCircle size={13} />
                      </div>
                    )}
                  </div>
                </div>

                {/* IMAGE + INPUT */}
                <div className="flex items-center gap-2">
                  <label className="group relative flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-white transition-all hover:bg-[#F8FBFF]">
                    {cert.certificate_image ? (
                      <img
                        src={cert.certificate_image}
                        alt="Certificate"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <FiUploadCloud className="text-blue-400" size={20} />
                    )}

                    <div className="absolute inset-0 hidden items-center justify-center bg-black/45 group-hover:flex">
                      <FiPlus
                        className="text-white"
                        size={16}
                        strokeWidth={3}
                      />
                    </div>

                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/webp"
                      className="hidden"
                      onChange={(e) =>
                        handleFileUpload(index, e.target.files?.[0], e)
                      }
                    />
                  </label>

                  <input
                    type="text"
                    value={cert.certificate_name}
                    onChange={(e) =>
                      handleChange(index, "certificate_name", e.target.value)
                    }
                    className="h-8 min-w-0 flex-1 rounded-md border border-gray-200 bg-white px-2 text-[10px] font-bold text-gray-800 outline-none transition placeholder:text-gray-300 focus:border-[#01C37D] focus:ring-1 focus:ring-[#01C37D]/10"
                    placeholder="e.g. ISO 9001:2015"
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default CertificatesSection;
